import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabase';

const { 
  FiMessageSquare, FiSend, FiUser, FiClock, FiSettings,
  FiRefreshCw, FiDatabase, FiAlertCircle, FiCheck,
  FiTrash2, FiEdit, FiPlus, FiSearch, FiFilter
} = FiIcons;

const { FaRobot } = FaIcons;

const AIChatAdmin = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatSettings, setChatSettings] = useState({
    enabled: true,
    welcomeMessage: "Hello! I'm here to help you find your perfect vehicle. How can I assist you today?",
    responseDelay: 1000,
    maxTokens: 500,
    temperature: 0.7,
    systemPrompt: "You are a helpful automotive sales assistant for Paradise-Performance dealership."
  });
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [newKnowledge, setNewKnowledge] = useState({ question: '', answer: '' });
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');

  useEffect(() => {
    loadChatData();
  }, []);

  // When a conversation is selected, load its messages
  useEffect(() => {
    if (selectedConversation?.id) {
      loadConversationMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const loadChatData = async () => {
    setLoading(true);
    try {
      // Load chat conversations from database
      const { data: chatData, error } = await supabase
        .from('ai_chat_sessions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (!error && chatData) {
        setConversations(chatData);
      } else {
        // No data available
        setConversations([]);
      }

      // Load knowledge base
      loadKnowledgeBase();
    } catch (error) {
      console.error('Error loading chat data:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };


  const loadConversationMessages = async (sessionId) => {
    setLoadingMessages(true);
    try {
      const { data, error } = await supabase
        .from('ai_chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (!error && Array.isArray(data)) {
        setMessages(data);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.warn('Error loading messages:', err?.message);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };


  const loadKnowledgeBase = async () => {
    try {
      // Load knowledge base from database
      const { data, error } = await supabase
        .from('ai_knowledge_base')
        .select('*')
        .order('usage_count', { ascending: false });
      
      if (data && !error) {
        setKnowledgeBase(data);
      } else {
        setKnowledgeBase([]);
      }
    } catch (error) {
      console.error('Error loading knowledge base:', error);
      setKnowledgeBase([]);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // Save settings to database
      console.log('Saving chat settings:', chatSettings);
      // Show success message
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const handleAddKnowledge = () => {
    if (editingKnowledge) {
      handleUpdateKnowledge();
    } else if (newKnowledge.question && newKnowledge.answer) {
      const entry = {
        id: knowledgeBase.length + 1,
        ...newKnowledge,
        category: 'Custom',
        usage_count: 0
      };
      setKnowledgeBase([...knowledgeBase, entry]);
      setNewKnowledge({ question: '', answer: '' });
    }
  };

  const handleEditKnowledge = (item) => {
    setEditingKnowledge(item);
    setNewKnowledge({ question: item.question, answer: item.answer });
  };

  const handleUpdateKnowledge = () => {
    if (editingKnowledge && newKnowledge.question && newKnowledge.answer) {
      setKnowledgeBase(knowledgeBase.map(item => 
        item.id === editingKnowledge.id 
          ? { ...item, question: newKnowledge.question, answer: newKnowledge.answer }
          : item
      ));
      setEditingKnowledge(null);
      setNewKnowledge({ question: '', answer: '' });
    }
  };

  const handleDeleteKnowledge = (id) => {
    setKnowledgeBase(knowledgeBase.filter(item => item.id !== id));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      default: return '😐';
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = (conv.visitor_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (conv.last_message || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">AI Chat Management</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('conversations')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'conversations' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Conversations
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'knowledge' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Knowledge Base
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'settings' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Settings
          </button>
        </div>
      </div>

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Stats Cards */}
          {(() => {
            const total = conversations.length;
            const active = conversations.filter(c => c.status === 'active').length;
            const pending = conversations.filter(c => c.status === 'pending').length;
            const closed = conversations.filter(c => c.status === 'closed').length;
            const totalMessages = conversations.reduce((sum, c) => sum + (c.message_count || 0), 0);
            const resolutionRate = total > 0 ? Math.round((closed / total) * 100) : 0;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <SafeIcon icon={FiMessageSquare} className="text-2xl text-primary-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{total}</p>
                  <p className="text-sm text-gray-600">Total Conversations</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <SafeIcon icon={FiUser} className="text-2xl text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{active}</p>
                  <p className="text-sm text-gray-600">Active Chats</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <SafeIcon icon={FiClock} className="text-2xl text-yellow-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{totalMessages}</p>
                  <p className="text-sm text-gray-600">Total Messages</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <SafeIcon icon={FiCheck} className="text-2xl text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{resolutionRate}%</p>
                  <p className="text-sm text-gray-600">Resolution Rate</p>
                </div>
              </div>
            );
          })()}

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <SafeIcon 
                  icon={FiSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          {/* Conversations List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visitor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Messages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sentiment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConversations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No conversations found. Conversations will appear here when visitors use the AI chat.
                    </td>
                  </tr>
                ) : filteredConversations.map(conv => (
                  <tr key={conv.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{conv.visitor_name}</div>
                          <div className="text-xs text-gray-500">{conv.visitor_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">{conv.last_message}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(conv.status)}`}>
                        {conv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {conv.message_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-2xl">
                      {getSentimentIcon(conv.sentiment)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(conv.created_at).toLocaleTimeString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedConversation(conv)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        View
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Conversation Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setSelectedConversation(null)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <div className="text-lg font-semibold text-gray-900">Conversation</div>
                <div className="text-sm text-gray-500">{selectedConversation.visitor_name} • {selectedConversation.visitor_id}</div>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-3 bg-gray-50">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-8 text-gray-500">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No messages found for this conversation.</div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`px-3 py-2 rounded-lg text-sm ${m.role === 'assistant' ? 'bg-white border' : 'bg-primary-600 text-white'}`}>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      <div className={`text-[11px] mt-1 ${m.role === 'assistant' ? 'text-gray-400' : 'text-primary-100'}`}>
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="px-6 py-4 border-t text-right bg-white">
              <button
                onClick={() => setSelectedConversation(null)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Base Tab */}
      {activeTab === 'knowledge' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Add New Knowledge */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Knowledge Entry</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Question"
                value={newKnowledge.question}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, question: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <textarea
                placeholder="Answer"
                value={newKnowledge.answer}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, answer: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleAddKnowledge}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold"
              >
                <SafeIcon icon={editingKnowledge ? FiCheck : FiPlus} className="w-4 h-4 inline mr-2" />
                {editingKnowledge ? 'Update Entry' : 'Add Entry'}
              </button>
              {editingKnowledge && (
                <button
                  onClick={() => {
                    setEditingKnowledge(null);
                    setNewKnowledge({ question: '', answer: '' });
                  }}
                  className="ml-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-semibold"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Knowledge Base List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Answer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {knowledgeBase.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No knowledge base entries yet. Add Q&A pairs above to help the AI answer common questions.
                    </td>
                  </tr>
                ) : knowledgeBase.map(item => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.question}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs">{item.answer}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.usage_count} times
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleEditKnowledge(item)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button 
                        onClick={() => handleDeleteKnowledge(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Chat Settings</h2>
            
            <div className="space-y-6">
              {/* Enable/Disable Chat */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900">Enable AI Chat</label>
                  <p className="text-sm text-gray-500">Toggle the AI chat feature on your website</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatSettings.enabled}
                    onChange={(e) => setChatSettings({ ...chatSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {/* Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Welcome Message
                </label>
                <textarea
                  value={chatSettings.welcomeMessage}
                  onChange={(e) => setChatSettings({ ...chatSettings, welcomeMessage: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* System Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  System Prompt
                </label>
                <textarea
                  value={chatSettings.systemPrompt}
                  onChange={(e) => setChatSettings({ ...chatSettings, systemPrompt: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-sm text-gray-500 mt-1">Define the AI assistant's personality and behavior</p>
              </div>

              {/* Advanced Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Response Delay (ms)
                  </label>
                  <input
                    type="number"
                    value={chatSettings.responseDelay}
                    onChange={(e) => setChatSettings({ ...chatSettings, responseDelay: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={chatSettings.maxTokens}
                    onChange={(e) => setChatSettings({ ...chatSettings, maxTokens: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Temperature (0-1)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={chatSettings.temperature}
                    onChange={(e) => setChatSettings({ ...chatSettings, temperature: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                  <SafeIcon icon={FiCheck} />
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AIChatAdmin;
